"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Badge,
  ScrollArea,
} from "visuais";
import type { ParcelasValidationResult } from "./rules/parcelasRules";

interface ParcelasRulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  validation: ParcelasValidationResult;
  isRegraAtiva: boolean;
  parcelasBaseProtheus?: Record<string, string>;
  inline?: boolean;
  className?: string;
}

function StatusBadge({ ok }: { ok: boolean }) {
  return (
    <Badge variant={ok ? "default" : "destructive"} className="text-[10px]">
      {ok ? "OK" : "ERRO"}
    </Badge>
  );
}

export function ParcelasRulesDialog({
  open,
  onOpenChange,
  validation,
  isRegraAtiva,
  parcelasBaseProtheus = {},
  inline = false,
  className,
}: ParcelasRulesDialogProps) {
  const issues = validation.issues;
  const parcelasBaseEntries = Array.from(
    new Set([...Object.keys(parcelasBaseProtheus || {})])
  ).sort((a, b) => a.localeCompare(b));
  const formatIsoToBr = (isoDate?: string) => {
    if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return isoDate || "-";
    const [year, month, day] = isoDate.split("-");
    return `${day}/${month}/${year}`;
  };
  const content = (
    <div className="space-y-3 text-sm">
      <div className="rounded-md border border-indigo-400 p-3">
        <div className="font-medium mb-2">Parcelas base do Protheus</div>
        {parcelasBaseEntries.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            Nenhuma parcela base disponível.
          </p>
        ) : (
          <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
            {parcelasBaseEntries.map((item) => (
              <div
                key={item}
                className="text-xs flex items-center justify-between gap-2 rounded border px-2 py-1"
              >
                <span className="font-medium">Parcela {item}</span>
                <div className="text-right">
                  <div className="text-muted-foreground">
                    {formatIsoToBr(parcelasBaseProtheus?.[item])}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-md border border-indigo-400 p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span>Base Protheus disponível por parcela</span>
          <StatusBadge ok={validation.ruleStatuses.hasBaseData} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span>Janela da base (D-1 ou datas após a base)</span>
          <StatusBadge ok={validation.ruleStatuses.respectsBaseWindow} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span>Limite da próxima parcela (até D-1 da próxima)</span>
          <StatusBadge ok={validation.ruleStatuses.respectsNextParcelaLimit} />
        </div>
      </div>

      <div className="rounded-md border border-indigo-400 p-3">
        <div className="font-medium mb-2">Erros encontrados</div>
        {issues.length === 0 ? (
          <p className="text-muted-foreground">
            Nenhum erro de regra encontrado.
          </p>
        ) : (
          <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
            {issues.map((issue) => (
              <div
                key={`${issue.item}-${issue.index}-${issue.message}`}
                className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs"
              >
                <span className="font-medium">Parcela {issue.item}: </span>
                <span>{issue.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (inline) {
    return (
      <div
        className={`w-full h-[70vh] rounded-sm border border-purple-400 bg-background/90 ${className ?? ""}`}
      >
        <ScrollArea className="h-full">
          <div className="p-4">
          <div className="mb-3">
            <h3 className="text-sm font-semibold">Regras de vencimento das parcelas</h3>
            <p className="text-xs text-muted-foreground">
              {isRegraAtiva
                ? "Confira abaixo se todas as regras estão sendo respeitadas."
                : "As regras de data consideram D-1 da base, datas após a base e no máximo D-1 da próxima parcela."}
            </p>
          </div>
          {content}
        </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Regras de vencimento das parcelas</DialogTitle>
          <DialogDescription>
            {isRegraAtiva
              ? "Confira abaixo se todas as regras estão sendo respeitadas."
              : "As regras de data consideram D-1 da base, datas após a base e no máximo D-1 da próxima parcela."}
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
